import { useCallback, useEffect, useRef, useState } from 'react';
import { getWikiUserChangePayload, stableSerialize } from '../services/wikiContentSerializer';
import { getWikiErrorMessage } from '../wiki.errors';

function useWikiAutosave({
  enabled,
  payload,
  onSave,
  delay = 30000,
}) {
  const [state, setState] = useState('saved');
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const initialSignature = useRef(null);
  const lastSavedSignature = useRef(null);
  const signatureRef = useRef(null);
  const inFlight = useRef(false);
  const queued = useRef(false);
  const timerRef = useRef(null);

  const signature = stableSerialize(getWikiUserChangePayload(payload));
  signatureRef.current = signature;
  const storageKey = payload?.id ? `sas-wiki-autosave-${payload.id}` : null;

  useEffect(() => {
    initialSignature.current = signatureRef.current;
    lastSavedSignature.current = signatureRef.current;
    setState('saved');
    setError('');
    setSavedAt(null);
  }, [payload?.id]);

  const runSave = useCallback(async () => {
    if (!enabled || !onSave || !payload?.id) {
      return;
    }

    if (inFlight.current) {
      queued.current = true;
      return;
    }

    if (signature === lastSavedSignature.current || signature === initialSignature.current) {
      return;
    }

    inFlight.current = true;
    queued.current = false;
    setState('saving');
    setError('');

    try {
      await onSave(payload);
      lastSavedSignature.current = signature;
      initialSignature.current = null;
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
      setSavedAt(new Date());
      setState('saved');
    } catch (saveError) {
      if (storageKey) {
        localStorage.setItem(storageKey, stableSerialize({
          savedAt: new Date().toISOString(),
          payload,
        }));
      }
      setError(getWikiErrorMessage(saveError, 'Echec de la sauvegarde'));
      setState(saveError.status === 409 ? 'conflict' : 'error');
    } finally {
      inFlight.current = false;
      if (queued.current) {
        queued.current = false;
        window.setTimeout(runSave, 0);
      }
    }
  }, [enabled, onSave, payload, signature, storageKey]);

  useEffect(() => {
    if (!enabled || !payload?.id) {
      return undefined;
    }

    if (signature === lastSavedSignature.current || signature === initialSignature.current) {
      return undefined;
    }

    setState('dirty');
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(runSave, delay);

    return () => window.clearTimeout(timerRef.current);
  }, [delay, enabled, payload?.id, runSave, signature]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (state !== 'dirty' && state !== 'saving' && state !== 'error') {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state]);

  return { state, error, savedAt };
}

export default useWikiAutosave;
